import React from 'react';
import { translate } from 'translations/i18n';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
import { Button } from '@companycam/slab-web';
import { tokens } from '@companycam/slab-tokens';
import { Text, type TextProps } from '@companycam/slab-web';

import { type ShowcaseProjectMediaItem } from 'javascripts/data/v3';

import { type WizardView } from '../constants';
import type { ShowcaseWizardFormData } from './Root';

const CardPreview = styled.div`
  flex: none;
  align-self: flex-start;
  width: 340px;
  margin-bottom: var(--cc_size_spacing_s);
`;

const CardPreviewInner = styled.div`
  overflow: hidden;
  border-radius: var(--cc_size_border_radius_xl);
  border: var(--cc_size_border_width_s) solid var(--cc_color_border_default);
`;

const ContentPreview = styled.div`
  padding: ${tokens.size_spacing_m}px;
`;

const ImagePreviewContainer = styled.div`
  position: relative;
`;

const ImagePreview = styled.div<{ path: string }>`
  width: 100%;
  height: 0;
  padding-bottom: 75%;
  background-image: url(${(props) => props.path});
  background-size: cover;
  background-position: center;
  background-color: ${tokens.color_background_3_le};
`;

const ChangePhotoContainer = styled.div`
  position: absolute;
  top: ${tokens.size_spacing_s}px;
  left: ${tokens.size_spacing_s}px;
`;

const TitlePreview = styled.div`
  overflow: hidden;
  margin-bottom: ${tokens.size_spacing_m}px;
  min-height: 20px;
  font-weight: 700;
  font-size: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const SmallHeader = styled(Text)<TextProps>`
  margin: 0 0 ${tokens.size_spacing_xs}px;
  text-transform: uppercase;
`;

const TruncatedList = styled(Text)<TextProps>`
  width: 100%;
  overflow: hidden;
  margin-bottom: 12px;
  min-height: 20px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CardPreviewPhotosContainer = styled.div`
  display: grid;
  grid-gap: ${tokens.size_spacing_s}px;
  grid-template-columns: repeat(5, 1fr);
  margin-top: ${tokens.size_spacing_l}px;
`;

const Placeholder = styled.div`
  width: 100%;
  padding-bottom: 100%;
  border-radius: ${tokens.size_border_radius_m}px;
  background: ${tokens.color_background_3_le};
  opacity: 0.3;
`;

const CardPreviewThumbnail = styled.div<{ path: string }>`
  width: 100%;
  height: 0;
  padding-bottom: 100%;
  background-size: cover;
  background-position: center;
  border-radius: ${tokens.size_border_radius_m}px;
  background-image: url(${({ path }) => path});
`;

type PreviewCardProps = {
  setView: (viewName: WizardView) => void;
  coverPhotoButtonVisible?: boolean;
  thumbnails: ShowcaseProjectMediaItem[];
};

type ListPreviewProps = {
  title: string;
  items?: string[];
};

const ListPreview = ({ items, title }: ListPreviewProps) => {
  return (
    <>
      <SmallHeader
        as="h6"
        forwardedAs="h6"
        family="heading"
        size="xxs"
        weight={600}
      >
        {title}
      </SmallHeader>
      <TruncatedList size="xs" forwardedAs="p" as="p">
        {items.join(', ')}
      </TruncatedList>
    </>
  );
};

type ThumbnailsPreviewProps = {
  items?: ShowcaseProjectMediaItem[];
};

const ThumbnailsPreview = ({ items = [] }: ThumbnailsPreviewProps) => {
  return (
    <CardPreviewPhotosContainer>
      {items.length === 0 ? (
        <>
          <Placeholder />
          <Placeholder />
          <Placeholder />
          <Placeholder />
          <Placeholder />
        </>
      ) : (
        <>
          {items.slice(0, 5).map((item) => (
            <CardPreviewThumbnail path={item.thumbnailUrl} key={item.id} />
          ))}
        </>
      )}
    </CardPreviewPhotosContainer>
  );
};

export const PreviewCard = ({
  setView,
  coverPhotoButtonVisible = true,
  thumbnails,
}: PreviewCardProps) => {
  const {
    values: { featuredImage, materials, title, types },
  } = useFormikContext<ShowcaseWizardFormData>();

  return (
    <CardPreview>
      <CardPreviewInner>
        <ImagePreviewContainer>
          <ImagePreview path={featuredImage?.assetPreviewLarge} />
          {coverPhotoButtonVisible ? (
            <ChangePhotoContainer>
              <Button
                color="secondary"
                size="small"
                icon={{
                  name: 'image-outline',
                  position: 'before',
                }}
                aria-label={translate(
                  'showcases.create.v2PublishWizard.main.coverPhotoButton',
                )}
                onClick={() => setView('SELECT_COVER_PHOTO')}
              >
                {translate(
                  'showcases.create.v2PublishWizard.main.coverPhotoButton',
                )}
              </Button>
            </ChangePhotoContainer>
          ) : null}
        </ImagePreviewContainer>
        <ContentPreview>
          <TitlePreview>{title}</TitlePreview>
          <ListPreview
            items={types}
            title={translate(
              'showcases.create.v2PublishWizard.main.types.label',
            )}
          />
          <ListPreview
            items={materials}
            title={translate(
              'showcases.create.v2PublishWizard.main.products.label',
            )}
          />
          <ThumbnailsPreview items={thumbnails} />
        </ContentPreview>
      </CardPreviewInner>
    </CardPreview>
  );
};
